import { ArrowForward } from 'assets/icons';
import Button from 'components/common/Button';
import Modal from 'components/common/Modal';
import ScrollToError from 'components/common/ScrollToError';
import { PROPERTY_VALUATION } from 'constants/constants';
import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { Col } from 'react-flexbox-grid';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import * as yup from 'yup';
import BuyingHouse from 'assets/images/quick-calculator/buying-house.svg?react';
import LandImg from 'assets/images/quick-calculator/land.svg?react';
import BuyingApartment from 'assets/images/quick-calculator/buying-apartment.svg?react';
import InlineSelect from 'components/inputs/InlineSelect';
import Checkbox from 'components/inputs/Checkbox';
import settings from 'config/settings';
import { Link as RouterLink } from 'react-router-dom';
import routePaths from 'router/route-paths';
import type { MeQuery } from 'graphql/generated/graphql';
import s from './QuickCalculator.module.scss';
import type { IQuickCalculatorProject } from './QuickCalculator.projects';

type TFormatMessage = IntlShape['formatMessage'];

/** The values of the quick calculator (the modal adds the planned project type and the consent). */
export interface IQuickCalculatorFormValues {
  name: string;
  /** Replaced by the chosen project type on submit (undefined until one is chosen). */
  type: string | undefined;
  country: string;
  consent: boolean;
  planned_project_type?: string | null;
  terms_of_use_consent?: boolean;
}

export type TQuickCalculatorSubmit = (
  values: IQuickCalculatorFormValues,
  formikActions?: FormikHelpers<IQuickCalculatorFormValues>,
) => Promise<void>;

const validationSchema = yup.object().shape({
  planned_project_type: yup
    .mixed()
    .notRequired()
    .when('type', {
      is: (type: string) => type === PROPERTY_VALUATION,
      then: (schema) =>
        schema.concat(
          yup
            .string()
            // The former code passed the message element to nullable(), which yup 0.28 read as `true`.
            .nullable()
            .required(() => <FormattedMessage id="errors.choose_one" />),
        ),
    }),
});

interface ILinkProps {
  href: string;
  label: string;
}

const Link = ({ href, label }: ILinkProps) => {
  const { formatMessage: t } = useIntl();
  return (
    <a href={href} rel="noopener noreferrer" target="_blank" style={{ textDecoration: 'none' }}>
      {t({ id: label })}
    </a>
  );
};

interface IInternalLinkProps {
  to: string;
  label: string;
}

const InternalLink = ({ to, label }: IInternalLinkProps) => {
  const { formatMessage: t } = useIntl();
  return (
    <RouterLink
      to={to}
      rel="noopener noreferrer"
      target="_blank"
      style={{ textDecoration: 'none' }}
    >
      {t({ id: label })}
    </RouterLink>
  );
};

interface IProjectCardProps extends IQuickCalculatorProject {
  handleSelect: (type: string) => void;
  t: TFormatMessage;
  isSubmitting: boolean;
}

export const ProjectCard = ({
  handleSelect,
  t,
  type,
  label,
  isSubmitting,
  img: Image,
  altText,
  xlSize = 3,
}: IProjectCardProps) => (
  <Col xs={12} md={6} xl={xlSize} className={s.projectCard}>
    <Image title={t({ id: altText || label })} />
    <h3>{t({ id: label })}</h3>
    <div className={s.btnWrapper}>
      <Button disabled={isSubmitting} onClick={() => handleSelect(type)} endIcon={<ArrowForward />}>
        {t({ id: 'app.start' })}
      </Button>
    </div>
  </Col>
);

interface IQuickCalculatorModalProps {
  hideModal: () => void;
  isVisible: boolean;
  t: TFormatMessage;
  isSubmitting: boolean;
  me: MeQuery['me'] | undefined;
  projectType: string | undefined;
  onSubmit: TQuickCalculatorSubmit;
  initialValues: IQuickCalculatorFormValues;
}

export const QuickCalculatorModal = ({
  hideModal,
  isVisible,
  t,
  isSubmitting,
  me,
  projectType,
  onSubmit,
  initialValues,
}: IQuickCalculatorModalProps) => (
  <Modal
    close={hideModal}
    isVisible={isVisible}
    size="md"
    headerText={t({ id: !me ? 'auth.modal_header' : 'project_types.property_valuation' })}
  >
    <Formik<IQuickCalculatorFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (
        args: IQuickCalculatorFormValues,
        formikActions: FormikHelpers<IQuickCalculatorFormValues>,
      ) => {
        const newValues = { ...args, type: projectType };
        await onSubmit(newValues, formikActions);
      }}
    >
      {() => (
        <Form>
          <ScrollToError />
          {projectType === PROPERTY_VALUATION ? (
            <Field
              name="planned_project_type"
              label={t({ id: 'property_valuation.planned_project_types.label' })}
              options={[
                {
                  img: BuyingApartment,
                  label: t({ id: 'property_valuation.planned_project_types.new_apartment' }),
                  description: t({
                    id: 'property_valuation.planned_project_types.new_apartment_description',
                  }),
                  value: 'new_apartment',
                },
                {
                  img: BuyingHouse,
                  label: t({ id: 'property_valuation.planned_project_types.buying_house' }),
                  description: t({
                    id: 'property_valuation.planned_project_types.buying_house_description',
                  }),
                  value: 'buying_house',
                },
                {
                  img: LandImg,
                  label: t({ id: 'property_valuation.planned_project_types.land_estimation' }),
                  description: t({
                    id: 'property_valuation.planned_project_types.land_estimation_description',
                  }),
                  value: 'land_estimation',
                },
              ]}
              component={InlineSelect}
            />
          ) : null}

          {!me ? (
            <>
              <p className="mt-4 mb-0">{t({ id: 'auth.terms_of_use_text' })}</p>

              <Field
                name="terms_of_use_consent"
                component={Checkbox}
                validate={(v: boolean | undefined) =>
                  !v ? <FormattedMessage id="auth.terms_of_use_required" /> : null
                }
                skipScrollingToField
                skipScrollingOnChange
                label={
                  <FormattedMessage
                    id="auth.terms_of_use"
                    values={{
                      link: (
                        <InternalLink
                          to={routePaths.termsOfUse}
                          label="auth.terms_of_use_link_label"
                        />
                      ),
                    }}
                  />
                }
              />
              <p className="text-left">
                <FormattedMessage
                  id="auth.privacy_policy"
                  values={{
                    data_processing_label: (
                      <InternalLink
                        to={routePaths.dataProcessing}
                        label="auth.data_processing_label"
                      />
                    ),
                    data_protection_label: (
                      <Link href={settings.dataProtectionUrl} label="auth.data_protection_label" />
                    ),
                  }}
                />
              </p>
            </>
          ) : null}

          <div className={s.submitBtn}>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              endIcon={<ArrowForward />}
              type="submit"
            >
              {t({ id: 'app.continue' })}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  </Modal>
);
